import { useCallback } from "react";
import { useSdkContext } from "scmp-sdk/components/contexts";
import type {
  GA4Event,
  SubscriptionEventCommonParameters,
} from "scmp-sdk/lib/google-analytics/ga4-types";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import { useMiniHeaderIndicatorContext } from "./subscription-button/context";

export const useGA4CommonParameters = () => {
  const { currentArticleId, rosettaInstance } = useSdkContext();

  const computeGA4CommonParameters = useCallback(() => {
    if (!rosettaInstance) return {};

    const baseParameters = rosettaInstance.rulesEngine.getOutcome(
      "tracking.ga4.baseParameters",
    ) as SubscriptionEventCommonParameters;

    const parameters: SubscriptionEventCommonParameters = {
      ...baseParameters,
      ...(currentArticleId ? { article_id: currentArticleId } : {}),
    };

    return parameters;
  }, [currentArticleId, rosettaInstance]);

  return { computeGA4CommonParameters };
};

interface UseSendImpressionEventsParameters {
  ga4Event: (() => GA4Event) | null;
}

export const useSendImpressionEvents = (parameters: UseSendImpressionEventsParameters) => {
  const { handleTrackGA4 } = useSdkContext();
  const { inMiniHeader } = useMiniHeaderIndicatorContext();

  const { ga4Event } = parameters;

  const { captureTrackImpressionEventTargetElement, intersection } =
    useTrackImpressionEventByIntersection<GA4Event>({
      ga4TrackingHandler: handleTrackGA4,
      getGa4Event: ga4Event,
      options: {
        intersectionThreshold: [0.5],
        isSendGA4Tracking: !inMiniHeader,
        shouldSendOnce: true,
      },
    });

  return {
    captureTrackImpressionEventTargetElement,
    intersection,
  };
};
